import React, { useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { signIn, signOut, confirmSignIn, signInWithRedirect, getCurrentUser, fetchUserAttributes } from '@aws-amplify/auth';
import {
  isAllowedStagingIdentity,
  isStagingLockdownActive,
  stagingAccessDeniedMessage,
} from '../../utils/stagingAccess';
import { useFormPalette } from '../pages/forms/DynamicFormPrimitives';
import { StyledTextField, StyledSubmitButton, fadeSlideUp, fadeIn } from '../pages/forms/PublicFormStyles';

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

function outlineBtnSx(p) {
  return {
    fontFamily: FONT, textTransform: 'none', fontWeight: 500, fontSize: '15px',
    borderRadius: '10px', height: '48px', width: '100%',
    borderColor: p.border, color: p.textPrimary,
    '&:hover': { borderColor: p.chipBorder, bgcolor: p.surfaceSubtle },
    '&:focus-visible': { boxShadow: `0 0 0 3px ${p.focusRing}` },
  };
}

function textBtnSx(p) {
  return {
    fontFamily: FONT, textTransform: 'none', fontWeight: 500, fontSize: '13px',
    color: p.textSecondary, '&:hover': { color: p.textPrimary, bgcolor: 'transparent' },
  };
}

function GatePage({ palette: p, children }) {
  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: p.pageBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      px: 2, py: 4, fontFamily: FONT,
    }}>
      <Box sx={{ width: '100%', maxWidth: 420, animation: `${fadeSlideUp} 400ms ease-out` }}>
        {children}
      </Box>
    </Box>
  );
}

function GateCard({ palette: p, children }) {
  return (
    <Box sx={{
      bgcolor: p.surface,
      border: `1px solid ${p.border}`,
      borderRadius: '20px',
      p: { xs: 3, sm: 4 },
      display: 'flex', flexDirection: 'column',
    }}>
      {children}
    </Box>
  );
}

function GateIcon({ palette: p, icon: Icon, muted }) {
  return (
    <Box sx={{
      width: 48, height: 48, borderRadius: '14px',
      bgcolor: p.chipBg, border: `1px solid ${p.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      mb: 2.5, mx: 'auto',
    }}>
      <Icon sx={{ fontSize: 22, color: muted ? p.textSecondary : p.textPrimary }} />
    </Box>
  );
}

function GateTitle({ palette: p, children }) {
  return (
    <Typography sx={{
      fontFamily: FONT, fontSize: '20px', fontWeight: 500,
      color: p.textPrimary, textAlign: 'center',
      letterSpacing: '-0.01em', lineHeight: 1.3,
    }}>
      {children}
    </Typography>
  );
}

function GateSubtitle({ palette: p, children }) {
  return (
    <Typography sx={{
      fontFamily: FONT, fontSize: '15px', color: p.textSecondary,
      textAlign: 'center', mt: 0.75, mb: 3, lineHeight: 1.6,
    }}>
      {children}
    </Typography>
  );
}

function ErrorText({ palette: p, children }) {
  if (!children) return null;
  return (
    <Box sx={{
      bgcolor: p.errorBg, border: `1px solid ${p.errorText}22`,
      borderRadius: '10px', px: 1.5, py: 1, mt: 1.5,
    }}>
      <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: p.errorText, lineHeight: 1.4 }}>
        {children}
      </Typography>
    </Box>
  );
}

export function InlineSignIn({ onSignedIn }) {
  const p = useFormPalette();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formState, setFormState] = useState('signIn');
  const [mfaCode, setMfaCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getCurrentUser()
      .then(() => { if (!cancelled) onSignedIn(); })
      .catch(() => { if (!cancelled) setCheckingSession(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSignIn = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setErrorMessage('Please enter your email and password.');
      return;
    }
    if (isStagingLockdownActive() && !isAllowedStagingIdentity(email.trim())) {
      setErrorMessage(stagingAccessDeniedMessage());
      return;
    }
    setLoading(true);
    setErrorMessage('');
    try {
      try {
        await getCurrentUser();
        await signOut();
      } catch (_) { /* no existing session — proceed */ }
      const { isSignedIn, nextStep } = await signIn({ username: email.trim(), password });
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setFormState('changePassword');
        setLoading(false);
        return;
      }
      const step = nextStep?.signInStep;
      if (step === 'CONFIRM_SIGN_IN_WITH_SOFTWARE_TOKEN_MFA' || step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setFormState('mfa');
        setLoading(false);
        return;
      }
      if (isSignedIn) {
        if (isStagingLockdownActive()) {
          const attributes = await fetchUserAttributes();
          const verifiedEmail = attributes?.email || email.trim();
          if (!isAllowedStagingIdentity(verifiedEmail)) {
            await signOut();
            setErrorMessage(stagingAccessDeniedMessage());
            setLoading(false);
            return;
          }
        }
        onSignedIn();
      } else {
        setErrorMessage(`Unsupported sign-in step: ${step || 'unknown'}`);
        setLoading(false);
      }
    } catch (error) {
      setErrorMessage(error.message || 'Sign-in failed.');
      setLoading(false);
    }
  };

  const handleMfa = async (e) => {
    if (e) e.preventDefault();
    if (mfaCode.length !== 6) {
      setErrorMessage('Enter all 6 digits.');
      return;
    }
    setLoading(true);
    setErrorMessage('');
    try {
      const result = await confirmSignIn({ challengeResponse: mfaCode });
      if (result.isSignedIn) {
        onSignedIn();
      } else {
        setErrorMessage('MFA verification failed.');
        setLoading(false);
      }
    } catch (error) {
      setErrorMessage(error.message || 'MFA verification failed.');
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    if (e) e.preventDefault();
    if (!newPassword.trim()) {
      setErrorMessage('Please enter a new password.');
      return;
    }
    setLoading(true);
    setErrorMessage('');
    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      const step = result?.nextStep?.signInStep;
      if (step === 'CONFIRM_SIGN_IN_WITH_SOFTWARE_TOKEN_MFA' || step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setFormState('mfa');
        setLoading(false);
        return;
      }
      if (result.isSignedIn) {
        onSignedIn();
      } else {
        setErrorMessage('Password change failed.');
        setLoading(false);
      }
    } catch (error) {
      setErrorMessage(error.message || 'Password change failed.');
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (isStagingLockdownActive()) {
      setErrorMessage('Google sign-in is disabled on this staging environment.');
      return;
    }
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      setErrorMessage(error.message || 'Google sign-in failed.');
    }
  };

  if (checkingSession) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} sx={{ color: p.textSecondary }} />
      </Box>
    );
  }

  if (formState === 'mfa') {
    return (
      <Box component="form" onSubmit={handleMfa} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: '15px', color: p.textSecondary, lineHeight: 1.6 }}>
          Enter the 6-digit code from your authenticator app.
        </Typography>
        <StyledTextField
          palette={p}
          placeholder="000000"
          value={mfaCode}
          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          autoFocus
          inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code', style: { letterSpacing: '0.3em', textAlign: 'center', fontWeight: 600 } }}
        />
        <ErrorText palette={p}>{errorMessage}</ErrorText>
        <StyledSubmitButton palette={p} type="submit" disabled={loading || mfaCode.length !== 6} disableElevation>
          {loading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : 'Verify code'}
        </StyledSubmitButton>
      </Box>
    );
  }

  if (formState === 'changePassword') {
    return (
      <Box component="form" onSubmit={handleChangePassword} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: '15px', color: p.textSecondary, lineHeight: 1.6 }}>
          You must set a new password before continuing.
        </Typography>
        <StyledTextField palette={p} placeholder="New password" type="password" value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)} autoFocus />
        <ErrorText palette={p}>{errorMessage}</ErrorText>
        <StyledSubmitButton palette={p} type="submit" disabled={loading} disableElevation>
          {loading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : 'Set password'}
        </StyledSubmitButton>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSignIn} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <StyledTextField palette={p} placeholder="Email" type="email" value={email}
        onChange={(e) => setEmail(e.target.value)} autoFocus />
      <StyledTextField palette={p} placeholder="Password" type="password" value={password}
        onChange={(e) => setPassword(e.target.value)} />
      <ErrorText palette={p}>{errorMessage}</ErrorText>
      <StyledSubmitButton palette={p} type="submit" disabled={loading} disableElevation>
        {loading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : 'Sign in'}
      </StyledSubmitButton>
      {/* Google sign-in temporarily disabled
      <Divider sx={{
        fontFamily: FONT, fontSize: '12px', color: p.textSecondary,
        '&::before, &::after': { borderColor: p.border },
      }}>
        or
      </Divider>
      <Button variant="outlined" onClick={handleGoogleSignIn} disableElevation sx={outlineBtnSx(p)}>
        Sign in with Google
      </Button>
      */}
    </Box>
  );
}

export function EmailMagicLinkGateScreen({ onRequestMagicLink }) {
  const p = useFormPalette();
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('enter_email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRequest = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setError('');
    try {
      await onRequestMagicLink(email.trim());
      setStep('pending');
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || 'Failed to send verification email.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GatePage palette={p}>
      <GateCard palette={p}>
        <GateIcon palette={p} icon={EmailOutlinedIcon} />
        <GateTitle palette={p}>
          {step === 'pending' ? 'Check your inbox' : 'Verify your email'}
        </GateTitle>
        <GateSubtitle palette={p}>
          {step === 'pending'
            ? <>We sent a verification link to <strong style={{ color: p.textPrimary }}>{email}</strong>. Click it to continue.</>
            : 'Enter your email to receive a secure verification link.'}
        </GateSubtitle>

        {step === 'enter_email' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <StyledTextField
              palette={p}
              placeholder="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRequest()}
              disabled={loading}
              autoFocus
            />
            <ErrorText palette={p}>{error}</ErrorText>
            <StyledSubmitButton palette={p} disabled={loading || !email.trim()} onClick={handleRequest} disableElevation>
              {loading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : 'Send verification link'}
            </StyledSubmitButton>
          </Box>
        )}

        {step === 'pending' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, animation: `${fadeIn} 300ms ease` }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: '12px',
              bgcolor: p.successBg, border: `1px solid ${p.successText}33`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5,
            }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 20, color: p.successText }} />
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: p.textSecondary, textAlign: 'center' }}>
              Didn't receive it? Check your spam folder.
            </Typography>
            <Button variant="text" size="small" onClick={() => { setStep('enter_email'); setError(''); }} sx={textBtnSx(p)}>
              Try a different email
            </Button>
          </Box>
        )}
      </GateCard>
    </GatePage>
  );
}

export function AuthGateScreen({ onSignedIn }) {
  const p = useFormPalette();
  return (
    <GatePage palette={p}>
      <GateCard palette={p}>
        <GateIcon palette={p} icon={LockOutlinedIcon} />
        <GateTitle palette={p}>Sign in to continue</GateTitle>
        <GateSubtitle palette={p}>Authentication is required to access this content.</GateSubtitle>
        <InlineSignIn onSignedIn={onSignedIn} />
      </GateCard>
    </GatePage>
  );
}

export function DisabledGateScreen() {
  const p = useFormPalette();
  return (
    <GatePage palette={p}>
      <GateCard palette={p}>
        <GateIcon palette={p} icon={BlockOutlinedIcon} muted />
        <GateTitle palette={p}>Access unavailable</GateTitle>
        <GateSubtitle palette={p}>This form is not currently accepting responses.</GateSubtitle>
      </GateCard>
    </GatePage>
  );
}

export function NotPublishedScreen() {
  const p = useFormPalette();
  return (
    <GatePage palette={p}>
      <GateCard palette={p}>
        <GateIcon palette={p} icon={BlockOutlinedIcon} muted />
        <GateTitle palette={p}>Form not available</GateTitle>
        <GateSubtitle palette={p}>
          This form has not been published yet. The workflow owner needs to publish the workflow before this form is accessible.
        </GateSubtitle>
      </GateCard>
    </GatePage>
  );
}

export function AccessDeniedScreen({ title, message }) {
  const p = useFormPalette();
  return (
    <GatePage palette={p}>
      <GateCard palette={p}>
        <GateIcon palette={p} icon={ErrorOutlineIcon} muted />
        <GateTitle palette={p}>{title || 'Access denied'}</GateTitle>
        <GateSubtitle palette={p}>
          {message || 'You do not have permission to access this content. The link may have expired or been revoked.'}
        </GateSubtitle>
      </GateCard>
    </GatePage>
  );
}
